import type { ClashBox } from '@viewer/clash/lib'
import { REDES, type Especialidade } from './clashEspecialidades'
import {
  arredondarAoPar,
  artigo,
  capitalizar,
  decimal,
  feminino,
  fixo,
  type FormaDoArtigo,
} from './clashGramatica'
import type { ClashPriority } from './clashTriage'

// ---------------------------------------------------------------------------
// Classificacao automatica dos conflitos: tipo, regra, prioridade,
// recomendacao, especialidade responsavel, a quem comunicar e nivel por
// cota.
//
// E o porte, para a plataforma, das regras com que o Cha139 foi triado
// fora dela (analise.py, 14/10/2026). Os textos e os limiares saem iguais
// aos do Python: o teste dourado (clashClassificacaoCha139.test.ts)
// compara-os par a par.
//
// Puro. Quem chama da, por conflito, os dois elementos ja descritos
// (`ClashElementoInfo`: especialidade, classe IFC, nome, tipo, diametro,
// caixa, eixos principais, volume) e o volume comum; o adaptador do
// viewer (clashClassificacaoDados.ts) le tudo isso dos modelos.
//
// Coordenadas: tudo num referencial com Z para CIMA (o do ficheiro IFC),
// em metros. A cota do nivel e a do ponto no mesmo referencial.
// ---------------------------------------------------------------------------

export type V3 = [number, number, number]

/** Eixos principais da geometria do elemento (PCA dos vertices). O eixo
 *  0 e o mais comprido; `lo`/`hi` sao a extensao em cada eixo, medida a
 *  partir do centro. */
export interface ClashPca {
  center: V3
  axes: [V3, V3, V3]
  lo: V3
  hi: V3
}

/** O que a classificacao precisa de saber de um elemento. */
export interface ClashElementoInfo {
  modelId: string
  localId: number
  guid?: string | null
  /** null: modelo sem especialidade conhecida. */
  especialidade: Especialidade | null
  /** Classe IFC em maiusculas, como o motor a da (IFCWALL, IFCBEAM). */
  categoria: string
  /** Name do IFC. */
  nome: string
  /** Nome do type (IfcTypeObject) ou ObjectType. */
  typeName?: string | null
  objectType?: string | null
  /** Propriedades de tamanho e sistema (Overall Size, Outside Diameter,
   *  Size, Diameter, System Name), tal como vem dos Psets. */
  props?: Record<string, string | number | null | undefined>
  /** Sistemas a que pertence (IfcSystem), se se souber. */
  sistemas?: string[]
  /** Piso declarado (IfcBuildingStorey). So informativo: o nivel e por
   *  cota. */
  piso?: string | null
  /** Volume, m3. */
  volume?: number | null
  /** Caixa (Z para cima). */
  bbox?: ClashBox | null
  pca?: ClashPca | null
}

/** Um conflito para classificar. */
export interface ClashParaClassificar {
  /** Elementos do par (a ordem nao conta: orienta-se aqui). */
  a: ClashElementoInfo
  b: ClashElementoInfo
  kind: 'hard' | 'clearance'
  depth?: number | null
  distance?: number | null
  /** Ponto do conflito (Z para cima). */
  point: V3
  /** Volume comum dos dois elementos (m3), por amostragem; null quando
   *  nao se calculou. */
  both?: number | null
}

/** Tipo de conflito (para o titulo e para as etiquetas). */
export type TipoDeConflito =
  | 'atravessamento'
  | 'folga'
  | 'duplicação'
  | 'entrega/apoio'
  | 'sobreposição'
  | 'interferência'
  | 'embebido'
  | 'duplicação/sobreposição'
  | 'fora do âmbito'

export interface ClassificacaoDoConflito {
  tipo: TipoDeConflito
  /** Codigo da regra (C1, A2, M2, D1, ...). */
  regra: string
  /** null: fora do ambito (nao se classifica). */
  prioridade: ClashPriority | null
  situacao: string
  recomendacao: string
  responsavel: string
  comunicar: string
  /** Nivel por cota. */
  nivel: string | null
  /** O elemento de rede e o hospedeiro, quando o par e rede x EST/ARQ. */
  rede: ClashElementoInfo | null
  hospedeiro: ClashElementoInfo | null
  /** Diametro exterior do elemento de rede, mm. */
  diametroMm: number | null
  /** Papel dos dois elementos, pela ordem EST, ARQ, MAC, HID, EEL. */
  papelA: string
  papelB: string
  especialidadeA: Especialidade | null
  especialidadeB: Especialidade | null
  /** Os dois elementos ("modelo:localId"), pela mesma ordem. */
  idA: string
  idB: string
}

// --- Configuracao (limiares e hierarquia, editaveis) -------------------------

export interface ClashRegrasConfig {
  /** Diametro a partir do qual atravessar viga ou pilar e Critico, mm. */
  diametroVigaPilarMm: number
  /** Diametro a partir do qual atravessar laje e Critico, mm. */
  diametroLajeMm: number
  /** Profundidade a partir da qual o conflito conta como real, mm. */
  profundidadeMm: number
  /** Folga abaixo da qual a folga e Media (F1), mm. */
  folgaCriticaMm: number
  /** Folga pedida entre redes (texto da recomendacao), mm. */
  folgaAlvoMm: number
  /** Tubo electrico abaixo deste diametro: furacao corrente (B5), mm. */
  diametroEelPequenoMm: number
  /** Ordem das redes: a primeira nao cede, a ultima cede a todas. */
  hierarquia: Especialidade[]
  /** Fraccao do elemento EST dentro do ARQ para duplicacao (D1). */
  fraccaoDuplicacao: number
  /** Fraccao do elemento EST dentro de uma parede ARQ, com o elemento a
   *  caber na espessura, para duplicacao (D2). */
  fraccaoDuplicacaoParede: number
  /** Fraccao do elemento de rede dentro do ARQ para "embebido" ou "corre
   *  por dentro" (B4, M6). */
  fraccaoEmbebido: number
  /** Posicao ao longo da viga a partir da qual e entrega (E1): 0 = meio,
   *  1 = topo. */
  entregaExtremo: number
  /** Pavimentos ARQ ate esta espessura (sufixo _E do tipo) sao
   *  revestimento, mm. */
  revestimentoPisoMm: number
  /** Paredes ARQ a partir desta espessura: confirmar se sao resistentes,
   *  mm. */
  paredeEspessaMm: number
  /** Folgas so entre redes MEP: as folgas com EST ou ARQ ficam fora do
   *  ambito. */
  folgasSoEntreRedes: boolean
  /** A prioridade de cada regra, quando difere da da tabela (o Rui muda-a
   *  no painel; os pareceres das regras seguem-na, os manuais ficam). */
  prioridades: Partial<Record<string, ClashPriority>>
  /** Rede a atravessar parede ARQ e Baixa (abertura ou roço, B6); sem
   *  isto, M2 como pavimentos e coberturas. Paredes espessas (talvez
   *  resistentes) ficam sempre M2. */
  paredeArqBaixa: boolean
  /** Cruzamento entre redes com menos disto e um pequeno ajuste (B7,
   *  Baixa), mm. 0 desliga. */
  ajustePequenoMm: number
  /** Marcadores de coordenacao (triangulo de coordenadas partilhadas,
   *  marcador de origem da federacao) ficam fora do ambito (X1). */
  ignorarMarcadores: boolean
  /** Tolerancia do nivel por cota: um ponto ate esta distancia abaixo da
   *  cota do piso ja conta como desse piso, m. */
  toleranciaNivelM: number
}

export const REGRAS_POR_OMISSAO: ClashRegrasConfig = {
  diametroVigaPilarMm: 50,
  diametroLajeMm: 100,
  profundidadeMm: 10,
  folgaCriticaMm: 25,
  folgaAlvoMm: 50,
  diametroEelPequenoMm: 100,
  hierarquia: ['HID', 'MAC', 'EEL'],
  fraccaoDuplicacao: 0.9,
  fraccaoDuplicacaoParede: 0.2,
  fraccaoEmbebido: 0.5,
  entregaExtremo: 0.7,
  revestimentoPisoMm: 30,
  paredeEspessaMm: 400,
  folgasSoEntreRedes: true,
  toleranciaNivelM: 0.45,
  // Calibracao do Rui (15/10/2026), depois do primeiro BCF do Cha139:
  // tubagem a atravessar laje e falta de negativa/courette, nao e
  // necessariamente grave; paredes resolvem-se com abertura ou roco;
  // pequenas intersecoes sao pequenos ajustes. So viga/pilar fica Critica.
  prioridades: { C2: 'media', A2: 'baixa', M5: 'baixa' },
  paredeArqBaixa: true,
  ajustePequenoMm: 50,
  ignorarMarcadores: true,
}

/** As regras tal como as do Python do ensaio de 14/10 (analise.py), para
 *  o teste dourado: sem a calibracao de 15/10. */
export const REGRAS_DO_ENSAIO_CHA139: ClashRegrasConfig = {
  ...REGRAS_POR_OMISSAO,
  hierarquia: [...REGRAS_POR_OMISSAO.hierarquia],
  prioridades: {},
  paredeArqBaixa: false,
  ajustePequenoMm: 0,
  ignorarMarcadores: false,
}

/** Le uma configuracao gravada; campo invalido fica com o valor por
 *  omissao. */
export function lerRegrasConfig(v: unknown): ClashRegrasConfig {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>
  const out: ClashRegrasConfig = {
    ...REGRAS_POR_OMISSAO,
    hierarquia: [...REGRAS_POR_OMISSAO.hierarquia],
    prioridades: { ...REGRAS_POR_OMISSAO.prioridades },
  }
  const numeros: (keyof ClashRegrasConfig)[] = [
    'diametroVigaPilarMm',
    'diametroLajeMm',
    'profundidadeMm',
    'folgaCriticaMm',
    'folgaAlvoMm',
    'diametroEelPequenoMm',
    'fraccaoDuplicacao',
    'fraccaoDuplicacaoParede',
    'fraccaoEmbebido',
    'entregaExtremo',
    'revestimentoPisoMm',
    'paredeEspessaMm',
    'toleranciaNivelM',
    'ajustePequenoMm',
  ]
  const destino = out as unknown as Record<string, unknown>
  for (const k of numeros) {
    const n = o[k]
    if (typeof n === 'number' && Number.isFinite(n) && n >= 0) destino[k] = n
  }
  if (typeof o.folgasSoEntreRedes === 'boolean') out.folgasSoEntreRedes = o.folgasSoEntreRedes
  if (typeof o.paredeArqBaixa === 'boolean') out.paredeArqBaixa = o.paredeArqBaixa
  if (typeof o.ignorarMarcadores === 'boolean') out.ignorarMarcadores = o.ignorarMarcadores
  if (o.prioridades && typeof o.prioridades === 'object') {
    const validas = new Set(['critica', 'alta', 'media', 'baixa'])
    out.prioridades = Object.fromEntries(
      Object.entries(o.prioridades as Record<string, unknown>).filter(
        ([k, v]) => /^[A-Z]\d$/.test(k) && typeof v === 'string' && validas.has(v),
      ),
    ) as Partial<Record<string, ClashPriority>>
  }
  if (Array.isArray(o.hierarquia)) {
    const h = o.hierarquia.filter((x): x is Especialidade => x === 'HID' || x === 'MAC' || x === 'EEL')
    if (h.length === 3 && new Set(h).size === 3) out.hierarquia = h
  }
  return out
}

// --- Tabela das regras (painel "Regras de classificacao") --------------------

export interface RegraDeClassificacao {
  regra: string
  prioridade: ClashPriority | null
  descricao: (c: ClashRegrasConfig) => string
}

const mmT = (n: number) => `${String(n).replace('.', ',')} mm`
const pctT = (f: number) => `${Math.round(f * 100)}%`

/** As regras, pela ordem de prioridade. O texto usa os limiares
 *  actuais. */
export const REGRAS: RegraDeClassificacao[] = [
  { regra: 'C1', prioridade: 'critica', descricao: (c) => `Viga ou pilar EST atravessado por conduta, tubagem ou tubo com Ø exterior >= ${mmT(c.diametroVigaPilarMm)} e profundidade >= ${mmT(c.profundidadeMm)}.` },
  { regra: 'C2', prioridade: 'critica', descricao: (c) => `Laje EST atravessada por conduta ou tubagem com Ø exterior >= ${mmT(c.diametroLajeMm)} e profundidade >= ${mmT(c.profundidadeMm)} (não há negativa no modelo EST).` },
  { regra: 'A1', prioridade: 'alta', descricao: (c) => `Viga ou pilar EST atravessado por elemento de rede com Ø < ${mmT(c.diametroVigaPilarMm)} (profundidade >= ${mmT(c.profundidadeMm)}).` },
  { regra: 'A2', prioridade: 'alta', descricao: (c) => `Laje EST atravessada por elemento de rede com Ø < ${mmT(c.diametroLajeMm)} (profundidade >= ${mmT(c.profundidadeMm)}): negativa a prever e validar.` },
  { regra: 'A3', prioridade: 'alta', descricao: (c) => `Equipamento de rede dentro de elemento EST (profundidade >= ${mmT(c.profundidadeMm)}).` },
  { regra: 'A4', prioridade: 'alta', descricao: () => 'Elemento de rede dentro de porta, escada ou guarda ARQ (não se resolve com negativa).' },
  { regra: 'A5', prioridade: 'alta', descricao: (c) => `Dois equipamentos de rede a ocupar o mesmo espaço (profundidade >= ${mmT(c.profundidadeMm)}).` },
  { regra: 'A6', prioridade: 'alta', descricao: (c) => `Cruzamento físico entre redes de especialidades diferentes (profundidade >= ${mmT(c.profundidadeMm)}).` },
  { regra: 'E4', prioridade: 'media', descricao: (c) => `Elemento EST não cabe na espessura do pavimento ou cobertura ARQ (sai acima ou abaixo do pacote); Baixa se < ${mmT(c.profundidadeMm)}.` },
  { regra: 'M1', prioridade: 'media', descricao: (c) => `Elemento de rede encostado a elemento EST com menos de ${mmT(c.profundidadeMm)} de interpenetração.` },
  { regra: 'M2', prioridade: 'media', descricao: (c) => `Conduta MAC ou tubagem HID (qualquer Ø), ou tubo eléctrico com Ø >= ${mmT(c.diametroEelPequenoMm)}, atravessa parede, pavimento ou cobertura ARQ: prever negativa, manga ou remate.` },
  { regra: 'M6', prioridade: 'media', descricao: (c) => `Conduta ou tubagem MAC/HID com mais de ${pctT(c.fraccaoEmbebido)} do volume dentro de parede ou pavimento ARQ (corre por dentro): confirmar courette, chaminé ou roço.` },
  { regra: 'B4', prioridade: 'baixa', descricao: (c) => `Tubo eléctrico com mais de ${pctT(c.fraccaoEmbebido)} do volume dentro de parede ou pavimento ARQ (roço ou enchimento).` },
  { regra: 'B5', prioridade: 'baixa', descricao: (c) => `Tubo eléctrico com Ø < ${mmT(c.diametroEelPequenoMm)} atravessa parede ou pavimento ARQ (furação corrente).` },
  { regra: 'M3', prioridade: 'media', descricao: (c) => `Parede modelada no MAC sobreposta a elementos ARQ (Baixa se < ${mmT(c.profundidadeMm)}).` },
  { regra: 'M4', prioridade: 'media', descricao: (c) => `Elemento de rede dentro de mobiliário, luminária ou equipamento ARQ (profundidade >= ${mmT(c.profundidadeMm)}).` },
  { regra: 'M5', prioridade: 'media', descricao: (c) => `Cruzamento entre redes com menos de ${mmT(c.profundidadeMm)}.` },
  { regra: 'E2', prioridade: 'media', descricao: (c) => `Viga EST atravessa parede ARQ fora da zona de apoio (Baixa se < ${mmT(c.profundidadeMm)}).` },
  { regra: 'E3', prioridade: 'media', descricao: (c) => `Pilar EST parcialmente embebido em parede ARQ (Baixa se < ${mmT(c.profundidadeMm)}).` },
  { regra: 'E5', prioridade: 'media', descricao: (c) => `Elemento EST interfere com porta, mobiliário ou equipamento ARQ (Baixa se < ${mmT(c.profundidadeMm)}).` },
  { regra: 'F1', prioridade: 'media', descricao: (c) => `Folga entre redes inferior a ${mmT(c.folgaCriticaMm)} (pedido: ${mmT(c.folgaAlvoMm)}).` },
  { regra: 'F2', prioridade: 'baixa', descricao: (c) => `Folga entre redes entre ${mmT(c.folgaCriticaMm)} e ${mmT(c.folgaAlvoMm)}.` },
  { regra: 'E1', prioridade: 'baixa', descricao: (c) => `Entrega ou apoio de viga (ponto no último ${pctT((1 - c.entregaExtremo) / 2)} de cada extremo do eixo) ou de laje EST numa parede ARQ.` },
  { regra: 'B1', prioridade: 'baixa', descricao: (c) => `Elemento de rede com menos de ${mmT(c.profundidadeMm)} dentro de elemento ARQ (sobreposição de poucos milímetros).` },
  { regra: 'B2', prioridade: 'baixa', descricao: () => 'Quadro ou caixa EEL embebido em parede ARQ (nicho a confirmar).' },
  { regra: 'B3', prioridade: 'baixa', descricao: (c) => `Elemento de rede atravessa revestimento ou acabamento ARQ (paredes de revestimento, pavimentos <= ${mmT(c.revestimentoPisoMm)}).` },
  { regra: 'D2', prioridade: 'baixa', descricao: (c) => `Duplicação de modelação: elemento EST cabe na espessura de um pavimento ou cobertura ARQ, ou de uma parede ARQ com >= ${pctT(c.fraccaoDuplicacaoParede)} do volume EST lá dentro.` },
  { regra: 'D1', prioridade: 'baixa', descricao: (c) => `Duplicação de modelação: elemento EST com >= ${pctT(c.fraccaoDuplicacao)} do volume dentro de uma parede, pavimento ou cobertura ARQ.` },
  { regra: 'G1', prioridade: 'media', descricao: (c) => `Outros pares (mesma especialidade, ou especialidade desconhecida): Média com profundidade >= ${mmT(c.profundidadeMm)}, Baixa abaixo.` },
  { regra: 'B6', prioridade: 'baixa', descricao: (c) => `Conduta, tubagem ou tubo atravessa parede ARQ com menos de ${mmT(c.paredeEspessaMm)} de espessura: abertura ou roço (paredes mais espessas, talvez resistentes, ficam M2).` },
  { regra: 'B7', prioridade: 'baixa', descricao: (c) => `Cruzamento entre redes com menos de ${mmT(c.ajustePequenoMm)}: pequeno ajuste de cota ou traçado.` },
  { regra: 'F0', prioridade: null, descricao: () => 'Folga com elemento EST ou ARQ: fora do âmbito quando as folgas são só entre redes (não se classifica).' },
  { regra: 'X1', prioridade: null, descricao: () => 'Marcador de coordenação (triângulo de coordenadas partilhadas, marcador de origem da federação): fora do âmbito.' },
]

/** A prioridade de uma regra com a configuracao actual (a da tabela, ou
 *  a que o utilizador escolheu). */
export function prioridadeDaRegra(regra: string, cfg: ClashRegrasConfig): ClashPriority | null {
  const base = REGRAS.find((r) => r.regra === regra)?.prioridade ?? null
  if (base === null) return null
  return cfg.prioridades?.[regra] ?? base
}

export const DESCRICAO_DA_REGRA: Record<string, (c: ClashRegrasConfig) => string> = Object.fromEntries(
  REGRAS.map((r) => [r.regra, r.descricao]),
)

/** Texto da hierarquia entre redes. */
export function textoDaHierarquia(h: readonly Especialidade[]): string {
  return `${h[0]} não cede; ${h[1]} cede à ${h[0]}; ${h[2]} cede a ${h[1]} e ${h[0]}.`
}

// --- Niveis por cota -------------------------------------------------------

export interface NivelDeReferencia {
  nome: string
  cota: number
}

/** O nivel de um ponto: o ultimo nivel cuja cota fica abaixo do ponto
 *  (com a tolerancia). Abaixo do primeiro, o primeiro. */
export function nivelPorCota(
  z: number,
  niveis: readonly NivelDeReferencia[],
  tolerancia = REGRAS_POR_OMISSAO.toleranciaNivelM,
): string | null {
  if (niveis.length === 0) return null
  const ord = [...niveis].sort((a, b) => a.cota - b.cota)
  let out = ord[0].nome
  for (const n of ord) if (z + tolerancia >= n.cota) out = n.nome
  return out
}

// --- Elementos: papel, tipo, diametro --------------------------------------

const ORDEM: Especialidade[] = ['EST', 'ARQ', 'MAC', 'HID', 'EEL']

/** Tipo do elemento: no Revit o Name e "Familia:Tipo:Id", e o tipo e o
 *  que fica sem o Id; senao, o nome do type. */
export function tipoDoElemento(e: ClashElementoInfo): string {
  const nome = e.nome ?? ''
  if ((nome.match(/:/g) ?? []).length >= 2) return nome.slice(0, nome.lastIndexOf(':'))
  return e.typeName || e.objectType || nome
}

/** Papel do elemento na leitura do conflito (viga, parede, conduta...). */
export function papelDoElemento(e: ClashElementoInfo, cfg: ClashRegrasConfig = REGRAS_POR_OMISSAO): string {
  const c = e.categoria.toUpperCase()
  const t = tipoDoElemento(e)
  switch (e.especialidade) {
    case 'EST':
      return ({ IFCBEAM: 'viga', IFCCOLUMN: 'pilar', IFCSLAB: 'laje' } as Record<string, string>)[c] ?? 'outro EST'
    case 'ARQ': {
      if (c === 'IFCWALL' || c === 'IFCWALLSTANDARDCASE') {
        if (/RevLadrilho|Reboco|Granito|Cimento|Guarni|Beirado|Vidro/.test(t)) return 'revestimento de parede'
        return 'parede'
      }
      if (t.includes('Stair') || c === 'IFCSTAIRFLIGHT') return 'escada'
      if (c === 'IFCSLAB') {
        if (t.startsWith('Basic Roof')) return 'cobertura'
        const m = /_E(\d+)/.exec(t)
        if (m && Number(m[1]) <= cfg.revestimentoPisoMm) return 'revestimento de piso'
        if (t.includes('Landing')) return 'patamar de escada'
        return 'pavimento'
      }
      return (
        ({
          IFCROOF: 'cobertura',
          IFCFURNITURE: 'mobiliário',
          IFCDOOR: 'porta',
          IFCSTAIRFLIGHT: 'escada',
          IFCBUILDINGELEMENTPROXY: 'equipamento/acessório ARQ',
          IFCLIGHTFIXTURE: 'luminária',
          IFCMEMBER: 'perfil (chapa)',
          IFCRAILING: 'guarda',
          IFCFLOWTERMINAL: 'aparelho sanitário/terminal',
          IFCDUCTSEGMENT: 'conduta (modelada no ARQ)',
          IFCPIPESEGMENT: 'tubagem (modelada no ARQ)',
        } as Record<string, string>)[c] ?? c
      )
    }
    case 'MAC':
      return (
        ({
          IFCFLOWSEGMENT: 'conduta',
          IFCDUCTSEGMENT: 'conduta',
          IFCFLOWFITTING: 'acessório de conduta',
          IFCDUCTFITTING: 'acessório de conduta',
          IFCCOVERING: 'isolamento de conduta',
          IFCWALLSTANDARDCASE: 'parede (modelada no MAC)',
          IFCWALL: 'parede (modelada no MAC)',
          IFCBUILDINGELEMENTPROXY: 'equipamento MAC',
        } as Record<string, string>)[c] ?? c
      )
    case 'HID':
      return (
        ({
          IFCFLOWSEGMENT: 'tubagem DAR',
          IFCPIPESEGMENT: 'tubagem DAR',
          IFCBUILDINGELEMENTPROXY: 'equipamento HID',
        } as Record<string, string>)[c] ?? c
      )
    case 'EEL':
      return (
        ({
          IFCFLOWSEGMENT: 'tubo eléctrico',
          IFCCABLECARRIERSEGMENT: 'tubo eléctrico',
          IFCFLOWFITTING: 'curva de tubo eléctrico',
          IFCCABLECARRIERFITTING: 'curva de tubo eléctrico',
          IFCBUILDINGELEMENTPROXY: 'quadro/caixa EEL',
        } as Record<string, string>)[c] ?? c
      )
    default:
      return c
  }
}

/** Maior numero (mm) de um tamanho Revit ("Ø140", "125 mm Ø", "200x100").
 *  Numeros sem unidade abaixo de 5 contam como metros. */
export function numeroMm(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined) return null
  if (typeof v === 'number') return v * (v < 5 ? 1000 : 1)
  const nums = [...String(v).matchAll(/\d+(?:[.,]\d+)?/g)].map((m) => Number(m[0].replace(',', '.')))
  return nums.length > 0 ? Math.max(...nums) : null
}

/** Seccao (as duas extensoes menores da PCA), m. */
function seccao(e: ClashElementoInfo): [number, number] | null {
  const p = e.pca
  if (!p) return null
  return [p.hi[1] - p.lo[1], p.hi[2] - p.lo[2]]
}

function comprimento(e: ClashElementoInfo): number | null {
  const p = e.pca
  return p ? p.hi[0] - p.lo[0] : null
}

/** Diametro (dimensao exterior da seccao) de um elemento de rede, mm. */
export function diametroMm(e: ClashElementoInfo): number | null {
  const p = e.props ?? {}
  for (const k of ['Overall Size', 'Outside Diameter', 'Size', 'Diameter']) {
    if (k in p) {
      const v = numeroMm(p[k])
      if (v && v > 5) return arredondarAoPar(v)
    }
  }
  const s = seccao(e)
  if (s) return arredondarAoPar(Math.max(...s) * 1000)
  return null
}

export function sistemaDoElemento(e: ClashElementoInfo): string | null {
  const s = e.props?.['System Name']
  if (typeof s === 'string' && s) return s
  return e.sistemas?.[0] ?? null
}

// --- Geometria ---------------------------------------------------------------

const dot = (a: readonly number[], b: readonly number[]) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]

/** Posicao do ponto ao longo do eixo principal: 0 = meio, 1 = topo. */
function posicaoAxial(e: ClashElementoInfo, p: V3): number | null {
  const pca = e.pca
  if (!pca) return null
  const ax = pca.axes[0]
  const t = dot([p[0] - pca.center[0], p[1] - pca.center[1], p[2] - pca.center[2]], ax)
  const lo = pca.lo[0]
  const hi = pca.hi[0]
  const half = (hi - lo) / 2 || 1e-9
  const mid = (hi + lo) / 2
  return Math.abs(t - mid) / half
}

/** Intervalo do elemento projectado na direccao n (caixa da PCA). */
function aoLongoDe(e: ClashElementoInfo, n: V3): [number, number] {
  const p = e.pca!
  let mid = dot(p.center, n)
  let half = 0
  for (let k = 0; k < 3; k++) {
    const a = dot(p.axes[k], n)
    mid += (a * (p.hi[k] + p.lo[k])) / 2
    half += (Math.abs(a) * (p.hi[k] - p.lo[k])) / 2
  }
  return [mid - half, mid + half]
}

/** O elemento EST cabe na espessura (eixo mais curto) do elemento ARQ? */
function cabeNaEspessura(est: ClashElementoInfo, arq: ClashElementoInfo, tol = 0.015): boolean {
  if (!est.pca || !arq.pca) return false
  const n = arq.pca.axes[2]
  const [lo, hi] = aoLongoDe(est, n)
  const c = dot(arq.pca.center, n)
  return lo >= c + arq.pca.lo[2] - tol && hi <= c + arq.pca.hi[2] + tol
}

function vertical(e: ClashElementoInfo): boolean {
  const s = seccao(e)
  return !!e.pca && Math.abs(e.pca.axes[0][2]) >= 0.7 && (comprimento(e) ?? 0) > Math.max(...(s ?? [0, 0])) * 1.5
}

function seccaoMm(e: ClashElementoInfo): string {
  const s = seccao(e)
  if (!s) return '?'
  const [a, b] = [...s].sort((x, y) => y - x)
  return `${fixo(a * 1000, 0)} x ${fixo(b * 1000, 0)} mm`
}

/** Abertura indicativa: Ø exterior + 50 mm, arredondada as dezenas. */
function abertura(d: number | null): number | null {
  return d ? Math.ceil((d + 50) / 10) * 10 : null
}

// --- Classificacao -------------------------------------------------------------

const LINEAR_REDE = new Set([
  'conduta',
  'acessório de conduta',
  'isolamento de conduta',
  'tubagem DAR',
  'tubo eléctrico',
  'curva de tubo eléctrico',
])

interface El extends ClashElementoInfo {
  papel: string
  tipo: string
  dim: number | null
  esp: Especialidade | null
}

function rotulo(e: El): string {
  return e.esp && e.papel.includes(e.esp) ? e.papel : `${e.papel} ${e.esp ?? e.modelId}`
}

function art(e: El, forma: FormaDoArtigo = 'o'): string {
  return artigo(e.papel, forma)
}

function ordem(e: Especialidade | null): number {
  return e ? ORDEM.indexOf(e) : ORDEM.length
}

/** Quem cede entre duas redes: [responsavel, a comunicar]. */
function quemCede(a: El, b: El, cfg: ClashRegrasConfig): [string, string] {
  const ra = a.esp ? cfg.hierarquia.indexOf(a.esp) : -1
  const rb = b.esp ? cfg.hierarquia.indexOf(b.esp) : -1
  if (ra >= 0 && rb >= 0) {
    const cede = ra > rb ? a : b
    const outro = cede === a ? b : a
    return [cede.esp!, outro.esp!]
  }
  return [a.esp ?? a.modelId, b.esp ?? b.modelId]
}

function respArt(esp: string, a: El, b: El, forma: FormaDoArtigo = 'o'): string {
  const el = a.esp === esp ? a : b
  return `${art(el, forma)} ${rotulo(el)}`
}

const PRIO_DE: Record<string, ClashPriority> = { 'Crítica': 'critica', Alta: 'alta', 'Média': 'media', Baixa: 'baixa' }

/** Marcadores de coordenacao que o Revit e os gabinetes poem na origem
 *  de cada modelo (no Cha139: "ETP-Shared Coordinates Triangle",
 *  "VN2R_GM_Marcador_Origem_Federacao", "MC_Federacao",
 *  "FluiMep-HPR-GM-Georreferenciação"). Chocam uns com os outros na
 *  origem e nao sao construcao. */
const MARCADOR = /shared coordinates|coordenadas partilhadas|georreferencia|georeferencia|marcador.?(de.?)?origem|federa[cç][aã]o|survey point|project base point/i

export function eMarcadorDeCoordenacao(e: Pick<ClashElementoInfo, 'nome' | 'typeName' | 'objectType'>): boolean {
  return [e.nome, e.typeName ?? '', e.objectType ?? ''].some((t) => MARCADOR.test(t))
}

/**
 * Classifica um conflito. `niveis` sao os niveis de referencia (cota no
 * mesmo referencial do ponto).
 */
export function classificarConflito(
  c: ClashParaClassificar,
  cfg: ClashRegrasConfig = REGRAS_POR_OMISSAO,
  niveis: readonly NivelDeReferencia[] = [],
): ClassificacaoDoConflito {
  const prep = (e: ClashElementoInfo): El => {
    const papel = papelDoElemento(e, cfg)
    const esp = e.especialidade
    return {
      ...e,
      papel,
      tipo: tipoDoElemento(e),
      esp,
      dim: esp && REDES.has(esp) ? diametroMm(e) : null,
    }
  }
  let A = prep(c.a)
  let B = prep(c.b)
  if (ordem(A.esp) > ordem(B.esp)) [A, B] = [B, A]
  const marcador = cfg.ignorarMarcadores ? [A, B].find(eMarcadorDeCoordenacao) : undefined
  const d = c.depth ?? 0
  const prof = cfg.profundidadeMm / 1000
  const both = c.both ?? 0
  const fracA = A.volume ? both / A.volume : null
  const nivel = nivelPorCota(c.point[2], niveis, cfg.toleranciaNivelM)
  const redeA = !!A.esp && REDES.has(A.esp)
  const redeB = !!B.esp && REDES.has(B.esp)
  let rede: El | null = null
  let hosp: El | null = null
  if (redeB && !redeA && A.esp) {
    rede = B
    hosp = A
  } else if (redeA && !redeB && B.esp) {
    rede = A
    hosp = B
  }
  const base = {
    nivel,
    rede: rede as ClashElementoInfo | null,
    hospedeiro: hosp as ClashElementoInfo | null,
    diametroMm: rede?.dim ?? null,
    papelA: A.papel,
    papelB: B.papel,
    especialidadeA: A.esp,
    especialidadeB: B.esp,
    idA: `${A.modelId}:${A.localId}`,
    idB: `${B.modelId}:${B.localId}`,
  }
  const sai = (
    tipo: TipoDeConflito,
    regra: string,
    prioridade: ClashPriority | string | null,
    situacao: string,
    recomendacao: string,
    responsavel: string,
    comunicar: string,
  ): ClassificacaoDoConflito => ({
    ...base,
    tipo,
    regra,
    prioridade:
      prioridade === null
        ? null
        : (cfg.prioridades?.[regra] ?? PRIO_DE[prioridade] ?? (prioridade as ClashPriority)),
    situacao,
    recomendacao,
    responsavel,
    comunicar,
  })
  const alvo = cfg.folgaAlvoMm

  // ---- Marcadores de coordenacao: nao sao construcao
  if (marcador) {
    return sai(
      'fora do âmbito',
      'X1',
      null,
      `Marcador de coordenação (${marcador.nome}) em conflito com ${(marcador === A ? B : A).papel}`,
      'Marcador de coordenação na origem do modelo: não é um conflito de construção.',
      A.esp ?? A.modelId,
      B.esp ?? B.modelId,
    )
  }

  // ---- Folgas
  if (c.kind === 'clearance') {
    const dist = c.distance ?? 0
    if (cfg.folgasSoEntreRedes && !(redeA && redeB)) {
      return sai(
        'fora do âmbito',
        'F0',
        null,
        `Folga entre ${A.papel} (${A.esp ?? A.modelId}) e ${B.papel} (${B.esp ?? B.modelId})`,
        'Folga com elemento EST ou ARQ: fora da verificação de folgas entre redes.',
        A.esp ?? A.modelId,
        B.esp ?? B.modelId,
      )
    }
    const [resp, val] = quemCede(A, B, cfg)
    const f1 = dist < cfg.folgaCriticaMm / 1000
    return sai(
      'folga',
      f1 ? 'F1' : 'F2',
      f1 ? 'Média' : 'Baixa',
      `Folga insuficiente: ${A.papel} (${A.esp}) a ${fixo(dist * 1000, 0)} mm de ${B.papel} (${B.esp})`,
      `Afastar ${respArt(resp, A, B)} para garantir pelo menos ${alvo} mm livres entre redes (montagem, isolamento e manutenção); hoje ${fixo(dist * 1000, 0)} mm.`,
      resp,
      val,
    )
  }

  // ---- EST x ARQ
  if (A.esp === 'EST' && B.esp === 'ARQ') {
    const fa = fracA ?? 0
    const pct = `${fixo(fa * 100, 0)}%`
    const pacote = ['pavimento', 'cobertura', 'revestimento de piso', 'patamar de escada']
    if (['parede', ...pacote].includes(B.papel) && fa >= cfg.fraccaoDuplicacao) {
      return sai(
        'duplicação',
        'D1',
        'Baixa',
        `${capitalizar(A.papel)} EST (${A.tipo}) contid${artigo(A.papel)} a ${pct} no volume de ${B.papel} ARQ (${B.tipo})`,
        'Duplicação de modelação: o elemento genérico do ARQ ocupa o volume da estrutura. Acordar a convenção (ARQ modela só as camadas não estruturais ou exclui a categoria na federação). Não é um conflito físico.',
        'ARQ',
        'EST, Coordenação BIM',
      )
    }
    if ((pacote.includes(B.papel) || (B.papel === 'parede' && fa >= cfg.fraccaoDuplicacaoParede)) && cabeNaEspessura(A, B)) {
      return sai(
        'duplicação',
        'D2',
        'Baixa',
        `${capitalizar(A.papel)} EST (${A.tipo}) dentro da espessura de ${B.papel} ARQ (${B.tipo}); ${pct} do volume EST sobreposto, o resto fica fora em planta`,
        'Duplicação de modelação: o pacote genérico do ARQ inclui a zona da estrutura (o elemento EST cabe na espessura do elemento ARQ). Acordar a convenção de modelação; não é um conflito físico.',
        'ARQ',
        'EST, Coordenação BIM',
      )
    }
    if ((A.papel === 'viga' || A.papel === 'laje') && (B.papel === 'parede' || B.papel === 'revestimento de parede')) {
      const t = A.papel === 'viga' ? posicaoAxial(A, c.point) : null
      if (A.papel === 'laje' || (t !== null && t >= cfg.entregaExtremo)) {
        return sai(
          'entrega/apoio',
          'E1',
          'Baixa',
          `Entrega de ${A.papel} EST (${A.tipo}) em ${B.papel} ARQ (${B.tipo})`,
          'Entrega/apoio do elemento estrutural na parede. Se for intencional: aceitar (sem acção) ou ARQ recortar o nicho na parede; EST confirma o comprimento de entrega.',
          'EST (confirma)',
          'ARQ',
        )
      }
      return sai(
        'sobreposição',
        'E2',
        d >= prof ? 'Média' : 'Baixa',
        `${capitalizar(A.papel)} EST (${A.tipo}) atravessa ${B.papel} ARQ (${B.tipo}) fora da zona de apoio`,
        'Verificar alinhamento entre a viga e a parede: ou a parede ARQ está fora do sítio, ou a viga atravessa a parede sem estar previsto. ARQ e EST acordam qual dos modelos corrige.',
        'ARQ / EST (a decidir)',
        'Coordenação BIM',
      )
    }
    if (A.papel === 'pilar' && (B.papel === 'parede' || B.papel === 'revestimento de parede')) {
      return sai(
        'sobreposição',
        'E3',
        d >= prof ? 'Média' : 'Baixa',
        `Pilar EST (${A.tipo}) parcialmente embebido em ${B.papel} ARQ (${B.tipo})`,
        'Confirmar a posição do pilar face à parede (pilar embebido ou a face a ficar saliente). ARQ ajusta a parede ou EST ajusta o pilar.',
        'ARQ / EST (a decidir)',
        'Coordenação BIM',
      )
    }
    if (pacote.includes(B.papel)) {
      return sai(
        'sobreposição',
        'E4',
        d >= prof ? 'Média' : 'Baixa',
        `${capitalizar(A.papel)} EST (${A.tipo}) sai parcialmente do pacote de ${B.papel} ARQ (${B.tipo})`,
        'O elemento EST não cabe na espessura do pavimento/cobertura ARQ (sai acima ou abaixo do pacote). Confirmar se é intencional (por exemplo viga ou pilar à vista, apoio da cobertura); se não for, ARQ revê cota/espessura do pacote ou EST revê a cota do elemento.',
        'ARQ',
        'EST',
      )
    }
    return sai(
      'interferência',
      'E5',
      d >= prof ? 'Média' : 'Baixa',
      `${capitalizar(A.papel)} EST (${A.tipo}) interfere com ${B.papel} ARQ (${B.tipo})`,
      'Reposicionar o elemento ARQ (porta, mobiliário, equipamento) para libertar o elemento estrutural.',
      'ARQ',
      'EST',
    )
  }

  // ---- EST/ARQ x rede
  if (rede && hosp) {
    const D = rede.dim
    const op = abertura(D)
    const Dt = D === null ? 'None' : String(D)
    const opt = op === null ? 'None' : String(op)
    if (hosp.esp === 'EST') {
      if (LINEAR_REDE.has(rede.papel)) {
        if (hosp.papel === 'viga' || hosp.papel === 'pilar') {
          const crit = D !== null && D >= cfg.diametroVigaPilarMm && d >= prof
          const regra = crit ? 'C1' : d >= prof ? 'A1' : 'M1'
          const prio = crit ? 'Crítica' : d >= prof ? 'Alta' : 'Média'
          const bot = hosp.bbox ? hosp.bbox.min[2] : null
          const perfil = /\b(HE[ABM]?|UPE|IPE)\s*(\d+)/.exec(hosp.tipo)
          const aco = /HE|UPE|IPE/.test(hosp.tipo)
          const altura = perfil ? Number(perfil[2]) : hosp.pca ? Math.round(Math.max(...(seccao(hosp) ?? [0])) * 100) * 10 : null
          const extra =
            aco && D && D >= 80
              ? ` Nota: perfil metálico com cerca de ${altura ?? '?'} mm de altura; atravessar a alma com esta dimensão em regra não é exequível.`
              : ''
          const recTxt = vertical(rede)
            ? `Prumada vertical a atravessar ${art(hosp)} ${hosp.papel}: deslocar ${art(rede)} ${rede.papel} (${rede.esp}, Ø${Dt} mm) em planta para livrar ${art(hosp)} ${hosp.papel} `
              + `(secção ${art(hosp, 'do')} ${hosp.papel} com cerca de ${seccaoMm(hosp)}). Furar ${art(hosp)} ${hosp.papel} na vertical só com verificação e aprovação da EST.${extra}`
            : `Desviar ${art(rede)} ${rede.papel} (${rede.esp}, Ø${Dt} mm) para baixo ${art(hosp, 'do')} ${hosp.papel} (cota mínima ${art(hosp, 'do')} ${hosp.papel}: ${bot === null ? '?' : decimal(bot)} m; em vigas inclinadas varia ao longo do eixo) ou contorná-${art(hosp, 'lo')}. `
              + `Se não houver altura livre, propor atravessamento Ø${opt} mm ${art(hosp, 'no')} ${hosp.papel}, a validar pela EST.${extra}`
          return sai(
            'atravessamento',
            regra,
            prio,
            `${capitalizar(rotulo(rede))} (Ø${Dt} mm) atravessa ${hosp.papel} EST (${hosp.tipo})`,
            recTxt,
            rede.esp!,
            'EST (valida), ARQ',
          )
        }
        if (hosp.papel === 'laje') {
          const crit = D !== null && D >= cfg.diametroLajeMm && d >= prof
          const regra = crit ? 'C2' : d >= prof ? 'A2' : 'M1'
          const prio = crit ? 'Crítica' : d >= prof ? 'Alta' : 'Média'
          const face = hosp.bbox ? decimal(hosp.bbox.min[2]) : '?'
          return sai(
            'atravessamento',
            regra,
            prio,
            `${capitalizar(rotulo(rede))} (Ø${Dt} mm) atravessa a laje EST (${hosp.tipo}) sem negativa`,
            vertical(rede)
              ? `Prever negativa na laje com cerca de Ø${opt} mm (dimensão indicativa: Ø exterior + 50 mm), a validar pela EST; ou desviar a prumada para uma courette/negativa existente.`
              : `${art(rede, 'O')} ${rede.papel} corre na horizontal dentro da espessura da laje: baixar ${art(rede)} ${rede.papel} para fora da laje (face inferior da laje a cerca de ${face} m), por exemplo no tecto falso. EST confirma que não há outra solução prevista.`,
            `${rede.esp} (propõe) / EST (dimensiona a negativa)`,
            'EST (valida), ARQ',
          )
        }
      }
      return sai(
        'interferência',
        'A3',
        d >= prof ? 'Alta' : 'Média',
        `${capitalizar(rotulo(rede))} interfere com ${hosp.papel} EST`,
        `Relocalizar ${art(rede)} ${rede.papel} para fora do elemento estrutural.`,
        rede.esp!,
        'EST',
      )
    }
    // Hospedeiro ARQ
    const hr = hosp.papel
    if (rede.papel === 'quadro/caixa EEL' && (hr === 'parede' || hr === 'revestimento de parede')) {
      return sai(
        'embebido',
        'B2',
        'Baixa',
        `Quadro/caixa EEL embebido em ${hr} ARQ (${hosp.tipo})`,
        'Equipamento embebido na parede: confirmar o nicho na parede ARQ (dimensões e profundidade disponíveis). Aceitar se previsto.',
        'EEL',
        'ARQ',
      )
    }
    if (rede.papel === 'parede (modelada no MAC)') {
      return sai(
        'duplicação/sobreposição',
        'M3',
        d >= prof ? 'Média' : 'Baixa',
        `Parede modelada no MAC (${rede.tipo}) sobreposta a ${hr} ARQ (${hosp.tipo})`,
        'Elemento de construção modelado na especialidade MAC. Confirmar se é uma parede/courette a incluir no ARQ; se for, ARQ modela-a e MAC retira-a do seu modelo ou passa a referência.',
        'MAC',
        'ARQ',
      )
    }
    if (d < prof) {
      return sai(
        LINEAR_REDE.has(rede.papel) ? 'atravessamento' : 'interferência',
        'B1',
        'Baixa',
        `${capitalizar(rotulo(rede))} toca ${hr} ARQ (${hosp.tipo}) em menos de ${cfg.profundidadeMm} mm`,
        'Sobreposição de poucos milímetros: ajustar a cota ou o afastamento do elemento MEP na próxima revisão; sem impacto em obra se confirmado.',
        rede.esp!,
        'ARQ',
      )
    }
    const fm = rede.volume ? both / rede.volume : 0
    const tuboEel = rede.esp === 'EEL' && (rede.papel === 'tubo eléctrico' || rede.papel === 'curva de tubo eléctrico')
    if (tuboEel && ['parede', 'pavimento', 'revestimento de parede', 'revestimento de piso'].includes(hr) && fm >= cfg.fraccaoEmbebido) {
      return sai(
        'embebido',
        'B4',
        'Baixa',
        `${capitalizar(rede.papel)} EEL (Ø${Dt} mm) embebid${artigo(rede.papel)} em ${hr} ARQ (${hosp.tipo})`,
        'Tubo eléctrico embebido (roço ou enchimento). Prática corrente: aceitar, confirmando que a espessura/enchimento do elemento ARQ o comporta.',
        'EEL',
        'ARQ',
      )
    }
    if ((rede.esp === 'MAC' || rede.esp === 'HID') && LINEAR_REDE.has(rede.papel) && (hr === 'parede' || hr === 'pavimento') && fm >= cfg.fraccaoEmbebido) {
      return sai(
        'atravessamento',
        'M6',
        'Média',
        `${capitalizar(rotulo(rede))} (Ø${Dt} mm) percorre o interior de ${hr} ARQ (${hosp.tipo})`,
        `${art(rede, 'O')} ${rede.papel} está mais de metade dentro do elemento ARQ, ao longo do seu eixo. Confirmar se existe courette, chaminé ou roço previsto (ARQ modela o vazio); se não existir, relocalizar a prumada/traçado.`,
        `${rede.esp} / ARQ (a decidir)`,
        'ARQ, EST se a parede for resistente',
      )
    }
    if (tuboEel && (D ?? 0) < cfg.diametroEelPequenoMm && (hr === 'parede' || hr === 'pavimento')) {
      return sai(
        'atravessamento',
        'B5',
        'Baixa',
        `${capitalizar(rede.papel)} EEL (Ø${Dt} mm) atravessa ${hr} ARQ (${hosp.tipo})`,
        'Atravessamento de pequeno diâmetro (furação corrente em obra). Aceitar ou ajustar o traçado; sem negativa dedicada.',
        'EEL',
        'ARQ',
      )
    }
    if (hr === 'revestimento de parede' || hr === 'revestimento de piso') {
      return sai(
        'atravessamento',
        'B3',
        'Baixa',
        `${capitalizar(rotulo(rede))} atravessa ${hr} ARQ (${hosp.tipo})`,
        'Atravessamento de revestimento/acabamento: prever furação ou remate; sem implicação estrutural.',
        rede.esp!,
        'ARQ',
      )
    }
    if (['parede', 'pavimento', 'cobertura', 'patamar de escada'].includes(hr)) {
      const s = seccao(hosp)
      const espessura = s ? Math.min(...s) * 1000 : null
      const what = hr === 'patamar de escada' ? 'patamar' : hr
      const rtxt =
        hr === 'cobertura'
          ? `Prever atravessamento da cobertura com cerca de Ø${opt} mm, com remate/chapéu e impermeabilização (ARQ pormenoriza), ou rever a saída ${art(rede, 'do')} ${rede.papel}.`
          : `Prever negativa/manga ${artigo(what, 'no')} ${what} ARQ com cerca de Ø${opt} mm (indicativo), ou ajustar o traçado ${art(rede, 'do')} ${rede.papel}. Se ${artigo(what)} ${what} for resistente, a EST valida.`
      const nota =
        hr === 'parede' && espessura && espessura >= cfg.paredeEspessaMm
          ? ` Parede com cerca de ${fixo(espessura, 0)} mm de espessura (LoadBearing não declarado como verdadeiro no IFC): confirmar se é resistente.`
          : ''
      if (hr === 'parede' && cfg.paredeArqBaixa && !nota) {
        return sai(
          'atravessamento',
          'B6',
          'Baixa',
          `${capitalizar(rotulo(rede))} (Ø${Dt} mm) atravessa parede ARQ (${hosp.tipo})`,
          `Abertura ou roço na parede ARQ com cerca de Ø${opt} mm, ou ajustar o traçado ${art(rede, 'do')} ${rede.papel}.`,
          `${rede.esp} (traçado) / ARQ (abertura)`,
          'ARQ',
        )
      }
      return sai(
        'atravessamento',
        'M2',
        'Média',
        `${capitalizar(rotulo(rede))} (Ø${Dt} mm) atravessa ${what} ARQ (${hosp.tipo})`,
        rtxt + nota,
        `${rede.esp} (traçado) / ARQ (negativa)`,
        'ARQ' + (nota ? ', EST' : ''),
      )
    }
    if (hr === 'porta' || hr === 'escada' || hr === 'guarda') {
      return sai(
        'interferência',
        'A4',
        'Alta',
        `${capitalizar(rotulo(rede))} interfere com ${hr} ARQ (${hosp.tipo})`,
        `Desviar ${art(rede)} ${rede.papel} para fora do vão/percurso (${hr}); não é resolúvel com negativa.`,
        rede.esp!,
        'ARQ',
      )
    }
    const posicao = rede.papel.includes('equipamento') || rede.papel.includes('quadro') ? 'a posição' : 'o traçado'
    return sai(
      'interferência',
      'M4',
      'Média',
      `${capitalizar(rotulo(rede))} interfere com ${hr} ARQ (${hosp.tipo})`,
      `Confirmar ${posicao} ${art(rede, 'do')} ${rede.papel} face ao elemento ARQ (${hr}): desviar, ou ARQ prevê recorte/espaço técnico no elemento.`,
      rede.esp!,
      'ARQ',
    )
  }

  // ---- Rede x rede
  if (redeA && redeB && A.esp !== B.esp) {
    const [resp, val] = quemCede(A, B, cfg)
    const equip = (e: El) => e.papel.includes('equipamento') || e.papel.includes('quadro')
    if (equip(A) || equip(B)) {
      return sai(
        'interferência',
        'A5',
        d >= prof ? 'Alta' : 'Média',
        `${capitalizar(rotulo(A))} sobrepost${artigo(A.papel)} a ${rotulo(B)}`,
        'Equipamentos ocupam o mesmo espaço: relocalizar um deles e confirmar acessos de manutenção.',
        resp,
        val,
      )
    }
    if (cfg.ajustePequenoMm > 0 && d >= prof && d < cfg.ajustePequenoMm / 1000) {
      return sai(
        'interferência',
        'B7',
        'Baixa',
        `${capitalizar(rotulo(A))} cruza ${rotulo(B)} em ${fixo(d * 1000, 0)} mm`,
        `Pequeno ajuste de cota ou traçado ${respArt(resp, A, B, 'do')} (${fixo(d * 1000, 0)} mm), com pelo menos ${alvo} mm livres.`,
        resp,
        val,
      )
    }
    return sai(
      'interferência',
      d >= prof ? 'A6' : 'M5',
      d >= prof ? 'Alta' : 'Média',
      `${capitalizar(rotulo(A))} interfere com ${rotulo(B)} (cruzamento ou troço sobreposto)`,
      `Ajustar a cota ou o traçado ${respArt(resp, A, B, 'do')} para passar acima/abaixo da outra rede, com pelo menos ${alvo} mm livres.`,
      resp,
      val,
    )
  }

  // ---- Outros pares (mesma especialidade, especialidade desconhecida)
  return sai(
    'interferência',
    'G1',
    d >= prof ? 'Média' : 'Baixa',
    `${capitalizar(rotulo(A))} interfere com ${rotulo(B)}`,
    'Confirmar qual dos elementos está fora do sítio e corrigir no modelo respectivo.',
    A.esp ?? A.modelId,
    B.esp ?? B.modelId,
  )
}

/** O feminino do papel, exportado para os titulos dos grupos. */
export { feminino }
